import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Download, Play } from "lucide-react";

export const REELS = [
  { slug: "get-paid-on-time", title: "Stop chasing payments", hook: "Still chasing clients for payment?" },
  { slug: "protect-your-scope", title: "Stop scope creep", hook: "\u201cThat wasn\u2019t in the quote.\u201d" },
  { slug: "client-admin-autopilot", title: "Put follow-ups on autopilot", hook: "What if client follow-ups ran themselves?" },
];

function VideoCard({ reel, index }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const v = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { v.play().catch(() => {}); } else { v.pause(); }
    }, { threshold: 0.6 });
    io.observe(v);
    return () => io.disconnect();
  }, []);
  const toggleSound = () => {
    const v = ref.current;
    v.muted = !muted; setMuted(!muted);
    if (muted) { v.currentTime = 0; v.play().catch(() => {}); }
  };
  const base = `/videos/scopeguard-${reel.slug}`;
  return (
    <figure className="snap-center shrink-0 w-[78vw] sm:w-auto sg-rise" style={{ animationDelay: `${index * 120}ms` }} data-testid={`reel-${reel.slug}`}>
      <div className="relative aspect-[9/16] rounded-2xl overflow-hidden border border-border bg-slate-950 group">
        <video ref={ref} poster={`${base}.jpg`} muted loop playsInline preload="metadata"
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="h-full w-full object-cover" data-testid={`reel-video-${reel.slug}`}>
          <source src={`${base}-web.mp4`} type="video/mp4" />
          <source src={`${base}-web.webm`} type="video/webm" />
        </video>
        {!playing && <button onClick={() => ref.current.play()} aria-label="Play video" data-testid={`reel-play-${reel.slug}`}
          className="absolute inset-0 flex items-center justify-center bg-black/20"><span className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center"><Play className="h-6 w-6 text-slate-900 ml-1" /></span></button>}
        <button onClick={toggleSound} aria-label={muted ? "Turn sound on" : "Mute"} data-testid={`reel-sound-${reel.slug}`}
          className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center hover:bg-black/80 transition-colors">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>
      <figcaption className="mt-3 flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold">{reel.title}</div>
          <div className="text-sm text-muted-foreground">{reel.hook}</div>
        </div>
        <a href={`${base}.mp4`} download data-testid={`reel-download-${reel.slug}`} aria-label={`Download ${reel.title} video`}
          className="shrink-0 h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-indigo-500 transition-colors"><Download className="h-4 w-4" /></a>
      </figcaption>
    </figure>
  );
}

export function VideoReel() {
  return (
    <div className="flex sm:grid sm:grid-cols-3 gap-5 sm:gap-8 overflow-x-auto snap-x snap-mandatory -mx-6 px-6 sm:mx-0 sm:px-0 pb-2" data-testid="video-reel">
      {REELS.map((r, i) => <VideoCard key={r.slug} reel={r} index={i} />)}
    </div>
  );
}
